import { useMemo } from "react";
import { Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import { formatWeight } from "@jackedlogs/shared";

import { machineLabel } from "../Logger/machineLabel";
import { patternLabel } from "../patternLabel";
import { PanelTitleRow } from "./InfoPill";
import { ErrorCard, LoadingCard } from "./panelCards";
import { makeTierOneStyles } from "./panelStyles";
import { DEFAULT_WINDOW_DAYS } from "./progressWindow";
import { TierOneTrendRow } from "./TierOneTrendRow";
import { rankE1RMTrends } from "./tierOneMath";
import type { ProgressDeps } from "./types";
import { useExerciseNameResolver } from "./useExerciseNameResolver";
import { useSessionResultsFromDeps } from "./useSessionResults";

// Top lift per movement pattern + sparkline + regression flag. The row itself
// (the three measurement-contract §3 states) is the shared TierOneTrendRow,
// which the reps-at-load axis renders too; this panel owns the e1RM data,
// the pattern grouping and its own i18n/units.
export function E1RMTrendsPanel(deps: ProgressDeps) {
  const { theme, units } = deps;
  const windowDays = deps.windowDays ?? DEFAULT_WINDOW_DAYS;
  const { t, i18n } = useTranslation();
  const { results, catalog, error } = useSessionResultsFromDeps(deps);
  const getName = useExerciseNameResolver(catalog);
  const styles = useMemo(() => makeTierOneStyles(theme), [theme]);
  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { day: "2-digit", month: "short" }),
    [i18n.language],
  );

  const ranked = useMemo(() => {
    if (!results || !catalog) return null;
    // Name resolved here via the live catalog (FK = slug); the aggregation
    // carries only the deleted-row snapshot.
    return rankE1RMTrends(results, catalog, Date.now(), windowDays).map((r) => ({
      ...r,
      name: getName(r.slug, r.snapshot),
    }));
  }, [results, catalog, getName, windowDays]);

  const trendsTitle = t("mobile.progress.trends.title", {
    defaultValue: "E1RM trends",
  });
  const trendsHelp = t("mobile.progress.trends.help", {
    days: windowDays,
    defaultValue:
      'For each movement pattern (squat, hinge, push, pull, …) shows the exercise where your top e1RM is highest. Chart = e1RM points over the selected {{days}}-day window (tap a point for its value). Arrow = trend over that window: ↗ progressing, → plateau, ↘ regressing. "Few data" badge appears when there are fewer than 3 points to fit a line.',
  });
  const period = t("progress.period.days", {
    defaultValue: "{{days}} d",
    days: windowDays,
  });

  if (error) return <ErrorCard theme={theme} titleKey="trends" />;
  if (ranked === null) return <LoadingCard theme={theme} titleKey="trends" />;

  if (ranked.length === 0) {
    return (
      <View style={styles.card}>
        <PanelTitleRow
          theme={theme}
          title={trendsTitle}
          body={trendsHelp}
          period={period}
        />
        <Text style={styles.empty}>
          {t("mobile.progress.empty", {
            defaultValue: "No recent activity to compare",
          })}
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.card}>
      <PanelTitleRow
        theme={theme}
        title={trendsTitle}
        body={trendsHelp}
        period={period}
      />
      {ranked.map((r) => (
        <TierOneTrendRow
          key={r.pattern}
          theme={theme}
          leadLabel={patternLabel(t, r.pattern)}
          name={r.name}
          // §3 insufficient: points exist but none went near the limit, so an
          // arrow would be a confident line through sets we know nothing about.
          insufficient={r.trustedCount === 0}
          reg={r.reg}
          value={formatWeight(r.latest, units, i18n.language)}
          notMeasurableText={t("mobile.progress.trends.notMeasurable", {
            name: r.name,
            defaultValue:
              "Not measurable yet: no {{name}} set went near its limit. A set taken close to failure (an AMRAP) would show your strength here.",
          })}
          fewDataText={t("mobile.progress.trends.fewData", {
            days: windowDays,
            defaultValue: "Few data points in {{days}} d",
          })}
          trendLabels={{
            // The same three words the panel's own help legend teaches
            // for ↗ → ↘, so the arrow says out loud what the legend says
            // on screen.
            up: t("mobile.progress.trends.a11yUp", { defaultValue: "progressing" }),
            flat: t("mobile.progress.trends.a11yFlat", { defaultValue: "plateau" }),
            down: t("mobile.progress.trends.a11yDown", { defaultValue: "regressing" }),
          }}
          // EQ-1-HIST slice 2 — only when the athlete actually named a machine.
          // Undefined for every untagged exercise, which is all of production
          // today, so those rows render byte-identically to before.
          machine={
            r.equipmentInstance === undefined
              ? undefined
              : {
                  label:
                    r.alternates.length > 0
                      ? t("mobile.progress.trends.machineMore", {
                          defaultValue: "{{machine}} · +{{count}}",
                          machine: machineLabel(r.equipmentInstance, t),
                          count: r.alternates.length,
                        })
                      : machineLabel(r.equipmentInstance, t),
                  // ⭐ The exercise rides along, the way the logger's machine
                  // chip already does it: a reader landing straight on this
                  // control by navigating between buttons would otherwise hear
                  // "Machine B" without knowing OF WHAT.
                  a11yLabel: t("mobile.progress.trends.machineA11y", {
                    defaultValue:
                      "{{exercise}}, {{machine}}. Show the other {{count}} machines.",
                    exercise: r.name,
                    machine: machineLabel(r.equipmentInstance, t),
                    count: r.alternates.length,
                  }),
                  alternates: r.alternates.map((a) => {
                    const value = formatWeight(a.latest, units, i18n.language);
                    // §3 on the alternate's OWN series (EQ-1-HIST-TRUST): when
                    // none of its points are a trusted floor, the row flags its
                    // value with the same "?" the headline uses.
                    const insufficient = a.trustedCount === 0;
                    return {
                      key: a.equipmentInstance,
                      // The bare machine name, not the qualified one: the
                      // exercise is already the line above these.
                      label: machineLabel(a.equipmentInstance, t),
                      value,
                      detail: t("mobile.progress.trends.machineSessions", {
                        defaultValue: "{{count}} sessions",
                        count: a.pointCount,
                      }),
                      insufficient,
                      // The "?" is decorative and hidden from a11y, so an
                      // untrusted alternate would otherwise read as a bare
                      // number to a screen reader — the meaning glyph-only. Give
                      // the value the same "not measurable" words the headline
                      // gets, so the untrusted state is spoken, not just seen.
                      ...(insufficient
                        ? {
                            valueA11yLabel: t(
                              "mobile.progress.trends.machineNotMeasurable",
                              {
                                defaultValue: "{{value}}, not measurable yet",
                                value,
                              },
                            ),
                          }
                        : {}),
                    };
                  }),
                }
          }
          chart={{
            key: r.slug,
            points: r.sorted.map((p) => ({
              x: p.atMs,
              y: p.kgPerRep1,
              label: dateFmt.format(new Date(p.atMs)),
            })),
            yFormat: (n) => formatWeight(n, units, i18n.language),
          }}
        />
      ))}
    </View>
  );
}
